import { CSVLink } from 'react-csv';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { AccordionDeprecated } from 'ui/accordion';
import { InfoBanner, WarningBanner } from 'ui/banner';
import { AnalyticsCardContainer, AnalyticsCardGroupContainer } from 'ui/card';
import { Divider } from 'ui/divider';
import { ChevronDown } from 'ui/icons';
import { InputText } from 'ui/inputs';
import { Link as UILink } from 'ui/links';
import PageHeader from 'ui/pageHeader/PageHeader';
import TableHeader from 'ui/tableHeader/TableHeader';
import TableRows from 'ui/tableRows/TableRows';
import {
  body1,
  body1Bold,
  body2,
  Body2,
  button,
  caption,
  colors,
  fontWeights,
  heading1,
  heading3,
  heading4,
  heading5,
  link2,
  smallShadow,
  spacing,
  subtitle1,
  subtitle2,
} from 'ui/theme';
import { Toggle } from 'ui/toggle';
import { pxToRem } from 'utils/convert';

export const Cell = styled.div`
  ${body2};
  box-sizing: border-box;
  display: table-cell;
  overflow-y: visible;
  padding: ${spacing.threeQuarter};
  margin: 0;
  height: ${spacing.x3};
  vertical-align: middle;
  &[data-content="snippet"] {
    [data-test="container"] {
      display: grid;
      grid-template-areas:
        "dynamic dynamic"
        "label value";
      grid-template-columns: auto auto;
      justify-items: center;
      align-content: center;
      gap: 0.5rem;
      [data-test="content"] {
        grid-area: dynamic;
        justify-self: left;
      }
      [data-test="label"] {
        ${caption}
        grid-area: label;
        justify-self: left;
        color: ${colors.textSecondary};
      }
      [data-test="id"] {
        ${caption}
        grid-area: value;
        color: ${colors.textPrimary};
      }
    }
  }
`;

export const StyledUILink = styled(UILink)`
  word-wrap: break-word;
  &[data-url-only-view="true"][data-is-analytics-bot-filtering-enabled="true"] {
    max-width: ${pxToRem(474)};
  }
  &[data-url-only-view="false"][data-is-analytics-bot-filtering-enabled="true"] {
    max-width: ${pxToRem(380)};
  }
  &[data-url-only-view="true"][data-is-analytics-bot-filtering-enabled="false"] {
    max-width: ${pxToRem(600)};
  }
  &[data-url-only-view="false"][data-is-analytics-bot-filtering-enabled="false"] {
    max-width: ${pxToRem(600)};
  }
`;

export const LoadingTableHeaderCell = styled.div<{ columnData: Record<string, any> }>`
  height: ${spacing.x3};
  background: red;
  padding: ${spacing.threeQuarter};
  &&& {
    z-index: 1;
    padding-left: ${spacing.double};
    width: 100%;
  }
`;

export const CenterText = styled.div`
  display: flex;
  height: 100%;
  align-items: center;
`;

export const RightAlignText = styled.div`
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: end;
`;

export const CleanAnchor = styled.a`
  ${link2}
`;

export const StyledRows = styled(TableRows)`
  > div:first-child {
    position: sticky;
    left: 0;
    align-items: center;
    width: ${spacing.x16};
    vertical-align: middle;
    background: inherit;
    padding-left: ${spacing.plusHalf};
    box-shadow: ${pxToRem(6)} 0 ${pxToRem(6)} -${pxToRem(6)} rgba(0, 0, 0, 0.12);
  }
`;

export const TooltipCardinalityTrigger = styled.div`
  display: flex;
  align-items: center;
`;

export const StyledHeader = styled(TableHeader)`
  > div:first-child {
    position: sticky;
    left: 0;
    width: ${spacing.x16};
    background: ${colors.white};
    padding-left: ${spacing.plusHalf};
    vertical-align: middle;
    z-index: 2;
  }
  > div {
    box-shadow: inset 0 0 ${colors.borderPrimary},
      inset 0 -${spacing.single} ${colors.borderPrimary};
    vertical-align: middle;
    height: ${spacing.x3};
  }
`;

export const TableWrapper = styled.div`
  max-height: 60vh;
  overflow: auto;
`;

export const StyledHeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.quarter};
`;

export const StyledPageActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.half};
`;

export const StyledLink = styled(Link)`
  ${link2}
`;

export const MenuWrapper = styled.div`
  display: inline-block;
  padding-left: ${spacing.base};
`;

export const MetricBreakdownHeaderContainer = styled.div`
  padding: ${spacing.plusHalf};
`;
export const MetricBreakdownHeaderTitle = styled.div`
  ${subtitle2};
  color: ${colors.textSecondary};
  font-weight: ${fontWeights.regular};
`;
export const MetricBreakdownHeaderValue = styled.div`
  ${heading3};
  font-feature-settings: 'tnum';
  margin-top: ${spacing.half};
`;

export const MetricBreakdownSecondaryListMetricName = styled.div`
  ${body2};
  color: ${colors.textSecondary};
  margin-right: ${spacing.half};
`;

export const MetricBreakdownSecondaryListMetricValue = styled.div`
  ${body1Bold};
`;

export const MetricBreakdownContainer = styled.div<{ grayscale?: boolean }>`
  background: ${(props) => (props.grayscale ? colors.grey1 : colors.white)};
  width: 100%;
`;

export const MetricList = styled.div<{ grayscale?: boolean }>`
  padding: ${spacing.half} ${spacing.plusHalf};
  &:nth-child(odd) {
    background: ${(props) => (!props.grayscale ? colors.white : colors.grey1)};
  }
  &:nth-child(even) {
    background: ${colors.grey1};
  }
`;

export const MetricRow = styled.div`
  ${body2};
  display: flex;
  justify-content: space-between;
`;

export const FullWidthMetricRow = styled(MetricRow)`
  width: 100%;
`;

export const SubmetricRow = styled.div`
  padding-left: ${spacing.plusHalf};
  border-left: 1px solid ${colors.grey15};
  ${body2};
`;

export const StyledDivider = styled(Divider)`
  margin: 0;
`;

export const StyledLearnMoreLinkWrapper = styled.div`
  padding-top: ${spacing.half};
`;

export const MetricBreakdownRow = styled.div`
  display: flex;
`;

export const MetricBreakdownHeaderRow = styled.div`
  display: flex;
  padding-bottom: ${spacing.x3};
`;

export const MetricBreakdownRowWithTwoEqualColumns = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
`;

export const MetricBreakdownRowWithTwoUnequalColumns = styled.div`
  display: grid;
  grid-template-columns: 2fr 3fr;
  align-items: center;
`;

export const OverviewCardContainer = styled.div`
  background: ${colors.white};
  width: 100%;
`;

export const WideCardContainer = styled(OverviewCardContainer)`
  display: flex;
  align-items: flex-start;
  height: 100%;
`;

export const WideCardLeft = styled.div<{ showBorder?: boolean }>`
  ${({ showBorder }) =>
    showBorder
      ? `
    border-right: 1px solid ${colors.neutral300};
  `
      : ''}

  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
`;

export const WideCardRight = styled.div`
  padding: ${spacing.plusHalf};
  flex: 1;
`;

export const LeftMetricSection = styled.div`
  margin: ${spacing.double} 0;
`;

export const LeftMetric = styled.div`
  border-top: 1px solid ${colors.grey15};
  padding: ${spacing.base} 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const StyledBody2 = styled(Body2)`
  color: ${colors.textSecondary};
`;

export const OverviewCardEqualWidthColContainer = styled.div`
  width: 50%;
`;

export const OverviewRowWithTwoEqualColumns = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  grid-column-gap: ${spacing.plusHalf};
`;

export const OverviewRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.base};
  width: 100%;
  padding: ${spacing.plusHalf};
`;

export const OverviewRowFullWidth = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.base};
  width: 100%;
`;

export const RetentionOverviewSmallMetrics = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${spacing.base};
`;

export const MetricBreakdownItem = styled.div`
  display: flex;
  align-items: center;
`;

export const MetricBreakdownContent = styled.div<{ noPadding?: boolean }>`
  width: 100%;
  ${({ noPadding }) =>
    !noPadding
      ? `
    padding: ${spacing.plusHalf};
  `
      : ''}
`;

export const MetricBreakdownSummaryContainer = styled.div`
  display: grid;
  gap: ${spacing.base};
  grid-template-columns: 1fr 1fr;
  grid-template-areas:
    'Main-Metric Main-Metric'
    'Sub-Mertric-1 Sub-Mertric-2';
  width: 100%;
  padding: ${spacing.plusHalf};
`;

export const MetricBreakdownToggletipContainer = styled.span`
  position: relative;
  display: inline-block;
  top: ${spacing.quarter};
  left: ${spacing.half};
`;

export const MetricBreakdownMainMetric = styled.div`
  grid-area: Main-Metric;
`;

export const MetricBreakdownMetricValue = styled.h1`
  ${heading1}
  margin: 0;
`;

const MetricBreakdownSubMetric = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;

export const MetricBreakdownSubMetricValue = styled.h3`
  ${subtitle1}
  margin: 0;
`;

const MetricBreakdownSubMetric1 = styled(MetricBreakdownSubMetric)`
  grid-area: Sub-Mertric-1;
`;

const MetricBreakdownSubMetric2 = styled(MetricBreakdownSubMetric)`
  grid-area: Sub-Mertric-2;
`;

export const MetricBreakdownSubMetricWrappers = [
  MetricBreakdownSubMetric1,
  MetricBreakdownSubMetric2,
];

export const MetricBreakdownMetricTitle = styled.p`
  ${body2};
  margin: 0;
  padding-right: ${spacing.half};
`;

export const GraphContainer = styled.div`
  padding: ${spacing.base};
`;

export const StyledCampaignAnalyticsCard = styled.div<{ fullWidth?: boolean; snippets?: boolean }>`
  background: ${colors.white};
  ${smallShadow};
  overflow: hidden;
  border-radius: ${spacing.half};
  margin-bottom: ${spacing.plusHalf};
  max-width: ${({ snippets }) => (snippets ? pxToRem(1138) : pxToRem(1128))};
  ${({ fullWidth }) =>
    fullWidth
      ? `
    flex: 1;
  `
      : ''}
`;

export const StyledLeftOverview = styled.div`
  padding: ${spacing.double};
  flex: 1 1 33%;
  min-height: ${spacing.x12};
`;

export const StyledRightOverview = styled.div`
  padding: ${spacing.double};
  flex: 1 1 66%;
  min-height: ${spacing.x12};
  overflow: hidden;
`;

export const StyledBreakdownCardHeader = styled.div`
  ${heading4};
  padding: ${spacing.base} ${spacing.plusHalf};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const SectionHeader = styled.div`
  ${heading3};
  margin-bottom: ${spacing.plusHalf};
`;

export const StyledChevronDown = styled(ChevronDown)`
  margin-left: ${spacing.half};
`;

export const StyledBannerContainer = styled.div`
  background-color: ${colors.purple100};
  padding: ${spacing.base} 0;
  margin-bottom: ${spacing.double};
`;

export const StyledBanner = styled.div`
  ${body1};
  color: ${colors.white};
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: ${pxToRem(1336)};
`;

export const DatePickerContainer = styled.div`
  ${body2};
  display: inline-block;
`;

export const DatePickerWrapper = styled.div`
  position: absolute;
  z-index: 2;
  background-color: ${colors.white};
  color: ${colors.textPrimary};
  border-radius: ${spacing.half};
`;
export const StyledMessageMedium = styled.span`
  ${caption};
  color: ${colors.textSecondary};
`;

export const StyledTitle = styled.span`
  padding-right: ${spacing.half};
`;

export const StyledLocationMapWrapper = styled.div`
  padding: ${spacing.plusHalf};
`;

export const StyledSelectLabel = styled.div`
  ${body2};
  margin-bottom: ${spacing.half};
`;

export const FilterCard = styled.div`
  background: ${colors.white};
  ${smallShadow};
  border-radius: ${spacing.half};
  margin-bottom: ${spacing.plusHalf};
  padding: ${spacing.plusHalf};
  display: flex;
  align-items: flex-end;
  gap: ${spacing.plusHalf};
`;

export const FilterCardHeader = styled.div`
  margin-bottom: ${spacing.base};
`;

export const SegmentSelector = styled.div`
  min-width: ${spacing.x24};
`;

export const SegmentMetricSelector = styled.div`
  min-width: ${spacing.x12};
`;

export const SelectWrapper = styled.div`
  > div {
    width: 100%;
  }
`;

export const StyledInfoBanner = styled(InfoBanner)`
  display: flex;
  align-items: center;
  margin-top: -${spacing.half};
  margin-bottom: ${spacing.plusHalf};
`;

export const StyledWarningBanner = styled(WarningBanner)`
  display: flex;
  align-items: center;
  margin-top: -${spacing.half};
  margin-bottom: ${spacing.plusHalf};
`;

export const CampaignAnalyticsCardHeader = styled.div`
  ${heading4};
  padding: ${spacing.base} ${spacing.plusHalf};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const CampaignAnalyticsCardTimeSeriesHeader = styled(CampaignAnalyticsCardHeader)`
  justify-content: flex-end;
`;

export const MainContentLayout = styled.div`
  display: flex;
  width: 100%;
  max-width: ${pxToRem(1336)};
  padding-bottom: ${spacing.doubleHalf};
`;

export const StyledPageHeader = styled(PageHeader)`
  padding: ${spacing.double} 0;
  margin: 0;
  width: 100%;
  max-width: ${pxToRem(1336)};
  flex-wrap: wrap;
  [data-test='page-header-page-sub-section'] {
    padding-top: ${spacing.threeQuarter};
  }
`;

export const ContentBody = styled.div`
  width: 100%;
`;

export const HiddenCSVLink = styled(CSVLink)`
  display: none;
`;

export const OverviewDeliveryChartsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(256px, 1fr));
  gap: ${spacing.plusHalf};
  padding: ${spacing.plusHalf};
  width: 100%;
`;

export const BarContainer = styled.div`
  height: ${spacing.double};
  width: 100%;
  background-color: ${colors.grey10};
  display: flex;
  justify-content: flex-start;
  border-radius: ${spacing.half};
  overflow: hidden;
`;

export const BarChartLegend = styled.div`
  margin-top: ${spacing.half};
  display: flex;
  gap: ${spacing.base};
`;

export const Bar = styled.div<{ percentage: number }>`
  background-color: ${(props) => props.color};
  height: 100%;
  width: ${(props) => props.percentage}%;
`;

export const Title = styled.div`
  ${body2}
  color: ${colors.textSecondary};
  display: flex;
  gap: ${spacing.quarter};
`;

export const Data = styled.span`
  ${heading4}
  color: ${colors.textSecondary};
  padding-left: ${spacing.half};
`;

export const DataLarge = styled.div`
  ${heading3}
  margin: ${spacing.half} 0 ${spacing.base};
  font-feature-settings: 'tnum';
`;

export const LegendItem = styled.span`
  ${caption};
  color: ${colors.textSecondary};
`;

export const StyledBullet = styled.svg`
  width: ${pxToRem(10)};
  height: ${pxToRem(10)};
  margin-right: ${spacing.half};
`;

export const StyledPage = styled.div`
  margin: ${pxToRem(-20)} ${pxToRem(-40)} ${pxToRem(-40)};
  padding-bottom: ${spacing.double};
  background: ${colors.grey1};
`;

export const SectionWrapper = styled.div<{ backgroundColor: string }>`
  display: flex;
  justify-content: center;
  padding: 0 ${spacing.double};
  background: ${({ backgroundColor }) => backgroundColor || 'inherit'};
`;

export const TagGroupWrapper = styled.div`
  display: inline-block;
  padding-left: ${spacing.half};
`;

export const StyledSubsection = styled.div`
  padding-top: ${spacing.threeQuarter};
  display: flex;
  align-items: baseline;
`;

export const MetricBreakdownCardEmptyState = styled.div`
  background: ${colors.white};
  text-align: center;
  padding: ${spacing.x3};
`;

export const EmptyStateContainer = styled.div`
  background: ${colors.grey1};
  text-align: center;
  padding: ${spacing.x3};
`;

export const EmptyStateTitle = styled.div`
  ${heading5}
  margin: ${spacing.half} 0;
`;

export const EmptyStateSubtitle = styled.div`
  ${body2}
  color: ${colors.textSecondary};
`;

export const PinnedMetricsEmptyStateContainer = styled.div`
  text-align: center;
  margin-bottom: ${spacing.x4};
`;

export const SkeletonWrapper = styled.div<{ height?: number; padding?: number }>`
  width: 100%;
  height: ${({ height }) => (height ? pxToRem(height) : spacing.x12)};
  padding: ${({ padding }) => (padding ? pxToRem(padding) : 0)};
`;

export const DeviceTableWrapper = styled.div`
  padding-top: ${spacing.base};
  padding-bottom: ${spacing.base};
`;

export const Row = styled.div`
  display: grid;
  align-items: flex-start;
  gap: ${spacing.plusHalf};
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
`;

export const CardColumn = styled.div`
  display: flex;
  flex-direction: column;
`;
export const SingleColRow = styled.div`
  display: flex;
`;

export const LeftCol = styled.div`
  flex: 1;
  min-width: ${pxToRem(371)};
`;

export const RightCol = styled.div`
  flex: 1;
`;

export const StyledListContainer = styled.div<{ useHorizontalCardPadding?: boolean }>`
  padding: ${({ useHorizontalCardPadding = false }) =>
    useHorizontalCardPadding ? `0 0 ${spacing.base} 0` : `${spacing.base} 0`};
`;

export const InfoBannerWrapper = styled.div`
  padding-bottom: ${spacing.plusHalf};
`;

export const StyledHeatmapContainer = styled.div`
  max-height: ${pxToRem(612)};
  margin: 0 ${spacing.base} ${spacing.base};
  z-index: 1;
  background: #e6e7e8; /** matches iframe default background */
  border: ${spacing.single} solid ${colors.borderPrimary} !important;
  box-shadow: none !important;
  border-radius: ${spacing.threeEights};
`;

export const HeatmapContentContainer = styled.div`
  margin: 0 auto;
`;

export const ClickBreakdownControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: ${spacing.base};
`;

export const RawDataToggle = styled(Toggle)`
  padding-left: ${spacing.base};
`;

export const RawDataWrapper = styled.div`
  display: flex;
  align-items: center;
  ${button};
  border: ${spacing.single} solid ${colors.grey15};
  padding: ${spacing.half};
  border-radius: ${spacing.quarter};
  cursor: pointer;
`;

export const HeatmapInfoWrapper = styled.div`
  padding: ${spacing.base};
  > div {
    align-items: center;
  }
`;

export const HeatmapInputText = styled(InputText)`
  width: ${pxToRem(231)};
  margin-left: ${spacing.threeQuarter};
`;

export const StyledHeatmapVisualization = styled.div`
  margin: 0 auto;
  left: 0;
  right: 0;
`;

export const StyledHeatmapMetrics = styled.svg<{ templateHeight: number; templateWidth: number }>`
  position: absolute;
  margin: 0 auto;
  left: 0;
  right: 0;
  top: 0;
  z-index: 10;
  height: ${(props) => pxToRem(props.templateHeight)};
  width: ${(props) => pxToRem(props.templateWidth)};
`;

export const StyledHeatmapThumbnail = styled.img`
  max-width: ${pxToRem(100)};
`;

export const StyledAnalyticsCardContainer = styled(AnalyticsCardContainer)`
  border: none;
  ${smallShadow};
  overflow: hidden;
`;

export const StyledAnalyticsCardGroupContainer = styled(AnalyticsCardGroupContainer)`
  margin: 0;
`;

export const AccordionButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: ${spacing.plusHalf} 0;
`;

export const AccordionButtonPlaceholder = styled.div`
  margin-bottom: ${spacing.plusHalf};
`;

export const CollapseWrapper = styled.div<{ isCollapsed?: boolean }>`
  margin-top: ${(props) => (props.isCollapsed ? 0 : spacing.plusHalf)};
`;

export const DateBarTimeContainer = styled.div`
  text-align: right;
  ${body2};
  color: ${colors.white};
`;

export const PinnedMetricsActionsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${spacing.plusHalf};
`;

export const BreakdownAccordion = styled(AccordionDeprecated)`
  background-color: inherit;
  > button,
  > div > div {
    align-items: center;
    margin-left: ${spacing.quarter};
    border-left: 1px solid ${colors.grey15};
    padding: 0;
  }
  // first AccordionLabel
  :first-child > button > div:first-child {
    padding-top: 0;
  }
  :first-child > button > div:nth-child(2) {
    margin-top: -${spacing.half};
  }
  // last AccordionLabel
  :last-child > button > div:first-child {
    padding-bottom: 0;
  }
  :last-child > button > div:nth-child(2) {
    margin-top: ${spacing.half};
  }
  // AccordionBody
  > div > div:nth-child(1) > div {
    padding: 0 ${spacing.quarter};
  }
  // last AccordionBody
  :last-child > div > div {
    padding-top: ${spacing.half};
  }
`;

export const BreakdownAccordionPlain = styled(BreakdownAccordion)`
  > button,
  > div > div {
    border-left: none;
  }
`;

export const BreakdownAccordionWrapper = styled.div`
  padding: ${spacing.base} 0;
`;

export const AccordionLabelRow = styled(MetricRow)`
  padding: ${spacing.half};
  width: 100%;
`;

export const AccordionBodyRow = styled(MetricRow)`
  padding: ${spacing.half} 0;
  width: 100%;
`;

export const StyledReasons = styled.span`
  ${body2};
  color: ${colors.textSecondary};
  padding-right: ${spacing.half};
`;

export const SecondMapContainer = styled.div`
  margin-top: ${spacing.base};
`;

export const AppNameWrapper = styled.span`
  margin-left: ${spacing.half};
`;

export const EmptyContainer = styled.div`
  display: grid;
  justify-items: center;
  align-content: center;
  grid-template-areas:
    'Empty-Image'
    'Empty-Info-Content'
    'Empty-Helper-Content';
  height: 100%;
  flex: 1;
`;

export const EmptyImage = styled.div`
  grid-area: Empty-Image;
`;

export const ConversionsCardContainer = styled.div`
  height: 100%;
  width: 100%;
  padding: ${spacing.plusHalf};
`;

export const ConversionsRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${spacing.plusHalf};
  height: 100%;
`;

export const ConversionsCardText = styled.p`
  ${subtitle1}
`;

export const EmptyInfoContent = styled.div`
  ${heading5}
  grid-area: Empty-Info-Content;
`;

export const EmptyHelperLink = styled.a`
  ${link2}
`;

// Revenue Breakdown Card
export const RevenueEmptyContainer = styled(EmptyContainer)`
  padding: ${spacing.plusHalf};
`;

export const EmptyInfoSubText = styled.div`
  ${body2}
  padding-top: ${spacing.half};
  color: ${colors.textSecondary};
  grid-area: Empty-Helper-Content;
  display: inline-block;
`;

export const EmptyHelperContent = styled(EmptyHelperLink)`
  grid-area: Empty-Helper-Content;
`;

export const TabularNumberFont = styled.div`
  font-feature-settings: 'tnum';
`;

export const SecondaryTabularNumberFont = styled(TabularNumberFont)`
  color: ${colors.textSecondary};
`;
